import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Home, FileText, Inbox, Building2, PlusCircle, ArrowUpRight, Activity } from "lucide-react";
import { fetchDashboardStats, fetchRecentForms, fetchRecentActivity } from "../api/stats.api.js";
import { Card, CardHeader, CardTitle, CardBody } from "../components/ui/Card.jsx";
import { Badge } from "../components/ui/Badge.jsx";
import { EmptyState } from "../components/ui/EmptyState.jsx";
import { Spinner } from "../components/ui/Spinner.jsx";
import { formatRelativeTime, formatNumber } from "../lib/format.js";
import { useAuthStore, selectUser } from "../store/auth.store.js";

const STAT_CARDS = (stats) => [
  {
    label: "Home Designs",
    icon: Home,
    value: stats?.designs?.total,
    sub: `${formatNumber(stats?.designs?.published ?? 0)} published`,
    href: "/designs",
  },
  {
    label: "Blog Posts",
    icon: FileText,
    value: stats?.blogs?.total,
    sub: stats?.blogs?.scheduled > 0 ? `${stats.blogs.scheduled} scheduled` : "all live",
    href: "/blogs",
  },
  {
    label: "Form Submissions (30d)",
    icon: Inbox,
    value: stats?.forms?.last30Days,
    sub: `${formatNumber(stats?.forms?.total ?? 0)} all-time`,
    href: "/forms",
    accent: true,
  },
  {
    label: "Display Homes",
    icon: Building2,
    value: stats?.displayHomes?.total,
    sub: "live listings",
    href: "/display-homes",
  },
];

const QUICK_ACTIONS = [
  { label: "New Design", href: "/designs/new" },
  { label: "New Blog Post", href: "/blogs/new" },
  { label: "New Facade", href: "/facades/new" },
  { label: "New Display Home", href: "/display-homes/new" },
];

export default function DashboardPage() {
  const user = useAuthStore(selectUser);

  useEffect(() => {
    document.title = "Dashboard · Admin";
  }, []);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: fetchDashboardStats,
    staleTime: 60_000,
  });

  const { data: recentForms = [] } = useQuery({
    queryKey: ["dashboard", "recent-forms"],
    queryFn: () => fetchRecentForms(5),
    staleTime: 30_000,
  });

  const { data: recentActivity = [] } = useQuery({
    queryKey: ["dashboard", "recent-activity"],
    queryFn: () => fetchRecentActivity(8),
    staleTime: 30_000,
    // Activity logs are super-admin only on the server; if the call 403s we
    // just hide the panel rather than surfacing an error.
    retry: false,
  });

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Overview</p>
          <h1 className="mt-1 font-display text-3xl text-text-primary">
            Welcome back, {user?.name?.split(" ")[0] ?? "Admin"}.
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            {"Here's what's happening at Shere-e-Punjab Homes today."}
          </p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {STAT_CARDS(stats).map((card) => (
          <Link
            key={card.label}
            to={card.href}
            className="group rounded-xl border border-border-subtle bg-bg-elevated p-5 transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-card-hover"
          >
            <div className="flex items-center justify-between">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                  card.accent ? "bg-accent/15 text-accent" : "bg-bg-elevated-2 text-text-secondary"
                }`}
              >
                <card.icon className="h-5 w-5" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-text-muted opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <p className="mt-4 text-xs uppercase tracking-widest text-text-muted">{card.label}</p>
            <p className="mt-1 font-display text-3xl text-text-primary">
              {statsLoading ? <Spinner size="sm" /> : formatNumber(card.value ?? 0)}
            </p>
            <p className="mt-1 text-xs text-text-secondary">{card.sub}</p>
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick create</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((a) => (
              <Link
                key={a.href}
                to={a.href}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-elevated-2 px-3 py-1.5 text-sm text-text-primary transition-colors hover:border-accent hover:text-accent"
              >
                <PlusCircle className="h-3.5 w-3.5" />
                {a.label}
              </Link>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Recent forms + activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent submissions</CardTitle>
            <Link to="/forms" className="text-xs text-text-secondary hover:text-accent">
              View all →
            </Link>
          </CardHeader>
          <CardBody className="px-0 py-0">
            {recentForms.length === 0 ? (
              <EmptyState
                icon={<Inbox className="h-5 w-5" />}
                title="No submissions yet"
                description="Forms submitted on the public site will appear here."
                className="border-0"
              />
            ) : (
              <ul className="divide-y divide-border-subtle">
                {recentForms.map((f) => (
                  <li key={f._id} className="flex items-start justify-between gap-3 px-6 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text-primary">
                        {f.data?.name ?? f.data?.email ?? "(no name)"}
                      </p>
                      <p className="truncate text-xs text-text-muted">
                        {f.formType.replace(/_/g, " ")} · {formatRelativeTime(f.createdAt)}
                      </p>
                    </div>
                    <StatusBadge status={f.status} />
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <Activity className="h-4 w-4 text-text-muted" />
          </CardHeader>
          <CardBody className="px-0 py-0">
            {recentActivity.length === 0 ? (
              <EmptyState
                icon={<Activity className="h-5 w-5" />}
                title="No activity logged yet"
                description="Admin actions across the system will be tracked here."
                className="border-0"
              />
            ) : (
              <ul className="divide-y divide-border-subtle">
                {recentActivity.map((a) => (
                  <li key={a._id} className="px-6 py-3 text-sm">
                    <p className="text-text-primary">
                      <span className="font-medium">
                        {a.user?.name ?? a.user?.email ?? "system"}
                      </span>{" "}
                      <span className="text-text-secondary">
                        {a.action} {a.resource}
                      </span>
                    </p>
                    <p className="text-xs text-text-muted">{formatRelativeTime(a.createdAt)}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const tone =
    status === "new"
      ? "accent"
      : status === "responded"
        ? "success"
        : status === "closed"
          ? "default"
          : "info";
  return <Badge tone={tone}>{status}</Badge>;
}
